import * as React from "react";
import { Box, Chip, Typography, SxProps, Theme } from "@mui/material";
import { GridApiCommunity } from "@mui/x-data-grid/internals";
import { StandaloneExport, CustomExportProps } from "./customExport";

/**
 *  Renders a consistent header for library pages,
 *  displaying the page title, an optional chip for additional context (e.g., item count),
 *  and optional right-aligned content (e.g., action buttons).
 * 	@rightContent can be used to pass in any React node, such as buttons or icons, that should be displayed on the right side of the header.
 *  LIBRARY_PAGE_HEADER_HEIGHT: optional use, if you need to reference the standard height of the header elsewhere.
 */

export const LIBRARY_PAGE_HEADER_HEIGHT = 76;

interface LibraryPageHeaderProps {
  title: string;
  chipLabel?: string;
  rightContent?: React.ReactNode;
  /** Pass a grid apiRef + csv/print options to render an Export button in the header */
  exportProps?: {
    apiRef: React.MutableRefObject<GridApiCommunity>;
    csvOptions?: CustomExportProps["csvOptions"];
    printOptions?: CustomExportProps["printOptions"];
  };
  /** Optional sx prop to customize the header styles */
  sx?: SxProps<Theme>;
}

export const LibraryPageHeader: React.FC<LibraryPageHeaderProps> = ({
  title,
  chipLabel,
  rightContent,
  exportProps,
  sx,
}) => {
  return (
    <Box
      sx={[
        {
          backgroundColor: "#ffffffff",
          width: "100%",
          px: 3,
          py: 2,
          overflow: "hidden",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          minHeight: `${LIBRARY_PAGE_HEADER_HEIGHT}px`,
          flexShrink: 0,
        },
        // Ensures sx prop is applied and properly merged
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            {title}
          </Typography>
          {chipLabel && (
            <Chip
              label={chipLabel}
              size="small"
              sx={{
                backgroundColor: "#DBEAFE",
                color: "#1E40AF",
                fontWeight: 500,
                fontSize: "0.875rem",
              }}
            />
          )}
        </Box>
        {(rightContent || exportProps) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {rightContent}
            {exportProps && (
              <StandaloneExport
                apiRef={exportProps.apiRef}
                csvOptions={exportProps.csvOptions}
                printOptions={exportProps.printOptions}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
